import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { InputModuleService } from '../../backend/inputModule';
import { AxiosError } from 'axios';

interface AttendanceConfigurationState {
    attendanceConfigurationDetails: {
        status: string,
        data: any,
        error: string | null
    },
    uploadAttendanceDetails:{
        status: string,
        data: any,
        error: string | null
    },
    deleteAttendanceDetails: {
        status: string,
        data: any,
        error: string | null
    },
    addAttendanceDetails: { 
        status: string,
        data: any,
        error: string | null
    },
    editAttendanceDetails: {
        status: string,
        data: any,
        error: string | null
    },
    bulkDeleteAttendanceDetails: {
        status: string,
        data: any,
        error: string | null
    }
}

const initialState: AttendanceConfigurationState = { 
    attendanceConfigurationDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    uploadAttendanceDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    deleteAttendanceDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    addAttendanceDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    editAttendanceDetails: {
        status: 'idle',
        data: '',
        error: null
    },
    bulkDeleteAttendanceDetails: {
        status: 'idle',
        data: '',
        error: null
    }
} as AttendanceConfigurationState

export const getAttendanceConfiguration = createAsyncThunk('attendanceConfiguration/getAttendanceConfiguration', async (data: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.fetchAttendanceConfiguration(data);
})

export const uploadAttendance = createAsyncThunk('attendanceConfiguration/uploadAttendance', async (data: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.uploadAttendanceConfiguration(data);
})

export const deleteAttendance = createAsyncThunk('attendanceConfiguration/deleteAttendance', async (id: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.deleteAttendanceConfiguration(id);
})

export const addAttendance = createAsyncThunk('attendanceConfiguration/addAttendance', async (data: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.addAttendanceConfiguration(data);
})

export const editAttendance = createAsyncThunk('attendanceConfiguration/editAttendance', async (data: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.editAttendanceConfiguration(data);
})

export const bulkDeleteAttendance = createAsyncThunk('Attendance/bulkDeleteAttendance', async (id: any) => {
    const inputModuleService = new InputModuleService();
    return await inputModuleService.bulkDeleteAttendance(id);
})

export const attendanceConfigurationSlice = createSlice({
    name: 'attendanceConfiguration',
    initialState,
    reducers: {
        resetGetAttendanceDetailsStatus: (state) => {
            state.attendanceConfigurationDetails.status = 'idle'
        },
        resetUploadAttendanceDetails :  (state) => {
            state.uploadAttendanceDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetDeleteAttendanceDetails: (state) => {
            state.deleteAttendanceDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetAddAttendanceDetails: (state) => {
            state.addAttendanceDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetEditAttendanceDetails: (state) => {
            state.editAttendanceDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
        resetBulkDeleteAttendanceDetails: (state) => {
            state.bulkDeleteAttendanceDetails = {
                status: 'idle',
                data: '',
                error: null
            }
        },
    },

    extraReducers: (builder) => builder
        .addCase(getAttendanceConfiguration.pending, (state) => {
            state.attendanceConfigurationDetails.status = 'loading'
        })
        .addCase(getAttendanceConfiguration.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.attendanceConfigurationDetails.status = 'failed'
                state.attendanceConfigurationDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.attendanceConfigurationDetails.status = 'succeeded'
                state.attendanceConfigurationDetails.data = action.payload.data
            }
        })
        .addCase(getAttendanceConfiguration.rejected, (state, action: any) => {
            state.attendanceConfigurationDetails.status = 'failed'
            state.attendanceConfigurationDetails.error = action.error.message
        })
        
        .addCase(uploadAttendance.pending, (state) => {
            state.uploadAttendanceDetails.status = 'loading'
        })
        .addCase(uploadAttendance.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.uploadAttendanceDetails.status = 'failed'
                state.uploadAttendanceDetails.error = action.payload
            } else {
                state.uploadAttendanceDetails.status = 'succeeded'
                state.uploadAttendanceDetails.data = action.payload
            }
        })
        .addCase(uploadAttendance.rejected, (state, action: any) => {
            state.uploadAttendanceDetails.status = 'failed'
            state.uploadAttendanceDetails.error = action.error.message
        })

        .addCase(deleteAttendance.pending, (state) => {
            state.deleteAttendanceDetails.status = 'loading'
        })
        .addCase(deleteAttendance.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.deleteAttendanceDetails.status = 'failed'
                state.deleteAttendanceDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.deleteAttendanceDetails.status = 'succeeded'
                state.deleteAttendanceDetails.data = action.payload.data
            }
        })
        .addCase(deleteAttendance.rejected, (state, action: any) => {
            state.deleteAttendanceDetails.status = 'failed'
            state.deleteAttendanceDetails.error = action.error.message
        })
        
        .addCase(addAttendance.pending, (state) => {
            state.addAttendanceDetails.status = 'loading'
        })
        .addCase(addAttendance.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.addAttendanceDetails.status = 'failed'
                state.addAttendanceDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.addAttendanceDetails.status = 'succeeded'
                state.addAttendanceDetails.data = action.payload.data
            }
        })
        .addCase(addAttendance.rejected, (state, action: any) => {
            state.addAttendanceDetails.status = 'failed'
            state.addAttendanceDetails.error = action.error.message
        })

        .addCase(editAttendance.pending, (state) => {
            state.editAttendanceDetails.status = 'loading'
        })
        .addCase(editAttendance.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.editAttendanceDetails.status = 'failed'
                state.editAttendanceDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.editAttendanceDetails.status = 'succeeded'
                state.editAttendanceDetails.data = action.payload.data
            }
        })
        .addCase(editAttendance.rejected, (state, action: any) => {
            state.editAttendanceDetails.status = 'failed'
            state.editAttendanceDetails.error = action.error.message
        })
        .addCase(bulkDeleteAttendance.pending, (state) => {
            state.bulkDeleteAttendanceDetails.status = 'loading'
        })
        .addCase(bulkDeleteAttendance.fulfilled, (state, action: any) => {
            if(action.payload instanceof AxiosError) {
                state.bulkDeleteAttendanceDetails.status = 'failed'
                state.bulkDeleteAttendanceDetails.error = (action.payload.response.data.error_messages || []).join(", ")
            } else {
                state.bulkDeleteAttendanceDetails.status = 'succeeded'
                state.bulkDeleteAttendanceDetails.data = action.payload.data
            }
        })
        .addCase(bulkDeleteAttendance.rejected, (state, action: any) => {
            state.bulkDeleteAttendanceDetails.status = 'failed'
            state.bulkDeleteAttendanceDetails.error = action.error.message
        })
})
  
export const {resetBulkDeleteAttendanceDetails, resetGetAttendanceDetailsStatus, resetUploadAttendanceDetails, resetDeleteAttendanceDetails, resetAddAttendanceDetails, resetEditAttendanceDetails } = attendanceConfigurationSlice.actions

export default attendanceConfigurationSlice.reducer